/** @jsxImportSource @emotion/react */
// 2026-09-27 Grid.tsx 에서 분리 — 데스크톱 렌더러. 동작은 그대로다.
import { css } from '@emotion/react'

import React from 'react'
import { ActionButton, View, Text } from '@components/atoms'
import { Modal } from '@components/molecules'
import { GridToolBar } from './GridToolBar'
import { GridSidePanel, SidePanelTab } from './GridSidePanel'
import { CircularProgress } from '@mui/material'
import {
	DataGrid,
	DataGridProps,
	GridValidRowModel,
	GridColDef,
	GridRowsProp,
	GridRowModel,
	GridRenderCellParams,
	useGridApiRef,
	GridPagination,
	useGridApiContext,
	useGridSelector,
	gridPageCountSelector,
	GridRenderEditCellParams,
	GridEditInputCell,
	GridEditBooleanCell,
	GridRowParams,
	GridRowSelectionModel,
	GridFilterOperator,
	GridPaginationModel,
} from '@mui/x-data-grid'
import { koKR } from '@mui/x-data-grid/locales'
import MuiPagination from '@mui/material/Pagination'
import { TablePaginationProps } from '@mui/material/TablePagination'
import { GridColumnType } from '@grid'
// `@mui/x-data-grid-generator`의 randomId 단순 대체 — 의존성 제거 목적
const randomId = (): string => Math.random().toString(36).substring(2, 11)

// 모든 Grid 인스턴스가 공유하는 페이지 사이즈 영속화 키
const PAGE_SIZE_STORAGE_KEY = 'judahs.grid.pageSize'
const PAGE_SIZE_OPTIONS = [25, 50, 100]
const DEFAULT_PAGE_SIZE = 25

const readSavedPageSize = (): number => {
	try {
		const raw = window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY)
		const n = raw ? Number(raw) : DEFAULT_PAGE_SIZE
		return PAGE_SIZE_OPTIONS.includes(n) ? n : DEFAULT_PAGE_SIZE
	} catch {
		return DEFAULT_PAGE_SIZE
	}
}

// 날짜 범위 필터 — Community에선 같은 컬럼에 multi-item이 안 먹어 단일 item으로 처리
const DATE_BETWEEN_OPERATOR: GridFilterOperator = {
	label: 'between',
	value: 'between',
	getApplyFilterFn: filterItem => {
		if (!filterItem.value) return null
		const { from, to } = filterItem.value as { from?: string; to?: string }
		if (!from && !to) return null
		const fromDate = from ? new Date(from + 'T00:00:00') : null
		const toDate = to ? new Date(to + 'T23:59:59.999') : null
		return cellValue => {
			if (cellValue == null) return false
			const v = cellValue instanceof Date ? cellValue : new Date(cellValue)
			if (Number.isNaN(v.getTime())) return false
			if (fromDate && v < fromDate) return false
			if (toDate && v > toDate) return false
			return true
		}
	},
}
import { styled } from '@mui/material/styles'
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'

import { mono, boFont, boSize } from '@components/systems/backoffice/backoffice.tokens'

export interface GridProps extends Omit<DataGridProps, 'columns'> {
	rows: GridRowsProp
	columns: GridColumnType
	loading?: boolean
	onAction?: (iudFlag: string, aRow: GridRowModel) => void
	onAdd?: (aRow: GridRowModel) => void
	onUpdate?: (aRow: GridRowModel) => void
	onDelete?: (aRow: GridRowModel) => void
	renderCell?: {
		[key: string]: (params: GridRenderCellParams) => JSX.Element
	}
	/**
	 * 수정 모드가 켜지면 **바로** 바뀌는 셀.
	 *
	 * `renderEditCell` 과 다르다 — 그쪽은 MUI 의 셀 편집 상태에 걸려 있어서
	 * 그 셀을 더블클릭해야 그려진다. 아바타 변경·패스워드 변경처럼 "입력"이 아니라
	 * "버튼"인 칸은 모드를 켜는 순간 보여야 하므로 여기에 둔다.
	 * 모드가 꺼져 있으면 평소의 `renderCell` 이 그려진다.
	 */
	renderEditModeCell?: {
		[key: string]: (params: any) => JSX.Element
	}
	renderEditCell?: {
		[key: string]: (params: GridRenderCellParams) => JSX.Element
	}
	isEditable?: boolean
	checkboxSelection?: boolean
	rowSelectionModel?: GridRowSelectionModel
	onRowSelectionModelChange?: (rowSelectionModel: GridRowSelectionModel) => void
	isRowSelectable?: (params: GridRowParams) => boolean
	sidePanel?: boolean
	title?: string
	onExcelUpload?: (rows: any[]) => void
	onExcelUploadClick?: () => void
	leftToolbarSlot?: React.ReactNode
	/** 'full'(기본) | 'search' — 검색만(수정 토글·추가·엑셀·인쇄·간격 없음). 고객 화면(/customer/wholesale)용 */
	toolbarVariant?: 'full' | 'search'
	/**
	 * 주면 index 열 바로 오른쪽에 "자세히" 열이 선다. 행의 다른 동작(체크박스·행 클릭)과 섞이지 않게
	 * 버튼이 이벤트를 삼킨다. idx·actions 처럼 순서 이동 대상이 아니다.
	 */
	onDetailClick?: (row: GridRowModel) => void
}

function Pagination({
	page,
	onPageChange,
	className,
}: Pick<TablePaginationProps, 'page' | 'onPageChange' | 'className'>) {
	const apiRef = useGridApiContext()
	const pageCount = useGridSelector(apiRef, gridPageCountSelector)

	return (
		<MuiPagination
			color="primary"
			className={className}
			count={pageCount}
			page={page + 1}
			onChange={(event, newPage) => {
				onPageChange(event as any, newPage - 1)
			}}
		/>
	)
}

function CustomPagination(props: any) {
	return <GridPagination ActionsComponent={Pagination} {...props} />
}

declare module '@mui/x-data-grid' {
	interface ToolbarPropsOverrides {
		setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void
		isLoading: boolean
		apiRef: React.MutableRefObject<any>
		onAddClick: () => void
		title?: string
		onExcelUpload?: (rows: any[]) => void
		onExcelUploadClick?: () => void
		leftToolbarSlot?: React.ReactNode
		toolbarVariant?: 'full' | 'search'
		editModeOn: boolean
		onEditModeToggle: (next: boolean) => void
		hasActionColumn: boolean
	}
}

/**
 * 데스크톱 렌더러 — 지금까지의 Grid 그대로(MUI DataGrid + 툴바 + 사이드 패널). 코드 이동만, 동작 불변.
 * 폭 등급 분기는 `Grid.tsx` 가 한다 (RESPONSIVE_PLAN §5.1).
 */
export const GridDesktop = (props: GridProps) => {
	// state
	const [rows, setRows] = React.useState(props.rows)
	const [showModal, setShowModal] = React.useState<boolean>(false)
	const [deleteRow, setDeleteRow] = React.useState<GridRowModel>({})
	const [editModeOn, setEditModeOn] = React.useState<boolean>(false)
	const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>(
		() => ({
			page: 0,
			pageSize: readSavedPageSize(),
		}),
	)
	const handlePaginationModelChange = React.useCallback((next: GridPaginationModel) => {
		setPaginationModel(next)
		try {
			window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(next.pageSize))
		} catch {
			/* noop */
		}
	}, [])

	// variables
	const isLoading: boolean = props.loading ?? false
	const isEditable: boolean = props.isEditable ?? true
	const checkboxSelection: boolean = props.checkboxSelection ?? false
	const sidePanelEnabled: boolean = props.sidePanel ?? true

	const [sidePanelTab, setSidePanelTab] = React.useState<SidePanelTab>(null)

	// 사용자 지정 컬럼 순서 (idx/actions 제외 user 컬럼들의 field 순서)
	const [columnOrder, setColumnOrder] = React.useState<string[]>(() =>
		props.columns.columns.map(c => c.field),
	)

	// props.columns 변경 시 새 컬럼 동기화 (기존 순서는 유지하되 사라진/새로 등장한 field 정리)
	React.useEffect(() => {
		const incoming = props.columns.columns.map(c => c.field)
		setColumnOrder(prev => {
			const kept = prev.filter(f => incoming.includes(f))
			const added = incoming.filter(f => !kept.includes(f))
			return [...kept, ...added]
		})
	}, [props.columns])

	const reorderColumn = React.useCallback((fromField: string, toField: string) => {
		setColumnOrder(prev => {
			const fromIdx = prev.indexOf(fromField)
			const toIdx = prev.indexOf(toField)
			if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return prev
			const next = [...prev]
			next.splice(fromIdx, 1)
			next.splice(toIdx, 0, fromField)
			return next
		})
	}, [])

	// 헤더 D&D — ag-grid 패턴: mousedown 후 5px 이상 이동해야 드래그 시작 (그 미만은 sort 클릭)
	const startHeaderDrag = React.useCallback(
		(downEvent: MouseEvent, fromField: string) => {
			if (downEvent.button !== 0) return
			const startX = downEvent.clientX
			const startY = downEvent.clientY
			let dragging = false
			let ghost: HTMLDivElement | null = null
			let lastTargetField: string | null = null

			const findHeaderField = (x: number, y: number): string | null => {
				const el = document.elementFromPoint(x, y) as HTMLElement | null
				const cell = el?.closest('.MuiDataGrid-columnHeader') as HTMLElement | null
				return cell?.getAttribute('data-field') ?? null
			}

			const onMove = (ev: MouseEvent) => {
				if (!dragging) {
					if (Math.abs(ev.clientX - startX) > 5 || Math.abs(ev.clientY - startY) > 5) {
						dragging = true
						document.body.style.userSelect = 'none'
						document.body.style.cursor = 'grabbing'
						ghost = document.createElement('div')
						const headerName =
							(apiRef.current as any)?.getColumn?.(fromField)?.headerName ?? fromField
						ghost.textContent = String(headerName)
						ghost.style.cssText = `position:fixed;pointer-events:none;z-index:9999;padding:6px 10px;background:${mono.ink};color:#fff;border-radius:2px;font-size:12px;font-family:${boFont.ui};letter-spacing:.04em;`
						document.body.appendChild(ghost)
					}
				}
				if (dragging) {
					if (ghost) {
						ghost.style.left = `${ev.clientX + 12}px`
						ghost.style.top = `${ev.clientY + 8}px`
					}
					const f = findHeaderField(ev.clientX, ev.clientY)
					if (f !== lastTargetField) {
						// 이전 hover 표시 제거
						document
							.querySelectorAll('.MuiDataGrid-columnHeader.colDropHover')
							.forEach(n => n.classList.remove('colDropHover'))
						lastTargetField = f
						if (f) {
							const cell = document.querySelector(
								`.MuiDataGrid-columnHeader[data-field="${f}"]`,
							)
							cell?.classList.add('colDropHover')
						}
					}
				}
			}

			const cleanup = () => {
				document.removeEventListener('mousemove', onMove)
				document.removeEventListener('mouseup', onUp)
				document.body.style.userSelect = ''
				document.body.style.cursor = ''
				if (ghost) {
					ghost.remove()
					ghost = null
				}
				document
					.querySelectorAll('.MuiDataGrid-columnHeader.colDropHover')
					.forEach(n => n.classList.remove('colDropHover'))
			}

			const onUp = (ev: MouseEvent) => {
				if (dragging) {
					ev.preventDefault()
					ev.stopPropagation()
					const targetField = findHeaderField(ev.clientX, ev.clientY)
					if (
						targetField &&
						targetField !== fromField &&
						targetField !== 'idx' &&
						targetField !== 'detail' &&
						targetField !== 'actions' &&
						targetField !== '__check__'
					) {
						reorderColumn(fromField, targetField)
					}
				}
				cleanup()
			}

			document.addEventListener('mousemove', onMove)
			document.addEventListener('mouseup', onUp)
		},
		[reorderColumn],
	)

	const apiRef = useGridApiRef()

	// sort 변경 시 헤더 아이콘이 갱신되도록 sortModel을 자체 state로 구독
	const [sortModel, setSortModel] = React.useState<
		Array<{ field: string; sort: 'asc' | 'desc' | null | undefined }>
	>([])

	React.useEffect(() => {
		const api: any = apiRef.current
		if (!api?.subscribeEvent) return
		const unsub = api.subscribeEvent('sortModelChange', (model: any[]) => {
			setSortModel(model ?? [])
		})
		return () => {
			if (typeof unsub === 'function') unsub()
		}
	}, [apiRef])

	const toggleSort = React.useCallback(
		(field: string) => {
			const cur = sortModel.find(s => s.field === field)?.sort ?? null
			const next = cur === 'asc' ? 'desc' : cur === 'desc' ? null : 'asc'
			apiRef.current?.setSortModel(next ? [{ field, sort: next }] : [])
		},
		[apiRef, sortModel],
	)

	// 헤더 cell 전체에 mousedown delegation으로 D&D 시작 (sort 영역과 동일한 잡힘 영역)
	React.useEffect(() => {
		const root = (apiRef.current as any)?.rootElementRef?.current as
			| HTMLElement
			| undefined
		if (!root) return
		const container = root.querySelector<HTMLElement>('.MuiDataGrid-columnHeaders')
		if (!container) return

		const handler = (e: MouseEvent) => {
			const target = e.target as HTMLElement
			const cell = target.closest('.MuiDataGrid-columnHeader')
			if (!cell) return
			const field = cell.getAttribute('data-field')
			if (
				!field ||
				field === 'idx' ||
				field === 'detail' ||
				field === 'actions' ||
				field === '__check__'
			)
				return
			// 메뉴 버튼/리사이즈 핸들/sort 버튼에서 시작한 경우 제외
			if (
				target.closest('.MuiDataGrid-menuIcon') ||
				target.closest('.MuiDataGrid-iconButtonContainer') ||
				target.closest('.MuiDataGrid-columnSeparator') ||
				target.closest('.gridSortBtn')
			)
				return
			startHeaderDrag(e, field)
		}

		container.addEventListener('mousedown', handler)
		return () => {
			container.removeEventListener('mousedown', handler)
		}
	}, [apiRef, startHeaderDrag, rows])

	// 사용자 스크롤 위치를 ref로 추적 — rows/loading 토글로 가상 스크롤러가
	// 잠시 사라졌다 다시 그려질 때도 마지막 위치로 복원하기 위함.
	const lastScrollRef = React.useRef<{ top: number; left: number }>({
		top: 0,
		left: 0,
	})

	const findScroller = React.useCallback((): HTMLElement | null => {
		const root = (apiRef.current as any)?.rootElementRef?.current as
			| HTMLElement
			| undefined
		return root?.querySelector<HTMLElement>('.MuiDataGrid-virtualScroller') ?? null
	}, [apiRef])

	// rows / loading 변경 시 가상 스크롤러가 reset되지 않도록 일정 시간 강제 복원
	React.useEffect(() => {
		setRows(props.rows)
		const target = lastScrollRef.current
		if (target.top === 0 && target.left === 0) return
		let cancelled = false
		let frames = 0
		const tick = () => {
			if (cancelled) return
			const s = findScroller()
			if (s && (s.scrollTop !== target.top || s.scrollLeft !== target.left)) {
				s.scrollTop = target.top
				s.scrollLeft = target.left
			}
			frames += 1
			if (frames < 30) requestAnimationFrame(tick)
		}
		requestAnimationFrame(tick)
		return () => {
			cancelled = true
		}
	}, [props.rows, props.loading, findScroller])

	// 가상 스크롤러에 scroll 리스너 — 사용자 마지막 위치를 ref에 누적
	React.useEffect(() => {
		const scroller = findScroller()
		if (!scroller) return
		const onScroll = () => {
			lastScrollRef.current = {
				top: scroller.scrollTop,
				left: scroller.scrollLeft,
			}
		}
		scroller.addEventListener('scroll', onScroll, { passive: true })
		return () => scroller.removeEventListener('scroll', onScroll)
	}, [findScroller, rows])

	// 수정 모드 토글에 따라 actions 컬럼 visibility 동기화
	React.useEffect(() => {
		if (apiRef.current?.getColumn?.('actions')) {
			apiRef.current.setColumnVisibility('actions', editModeOn)
		}
	}, [apiRef, editModeOn, rows])

	const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
		<Tooltip {...props} classes={{ popper: className }} />
	))(({ theme }) => ({
		[`& .${tooltipClasses.tooltip}`]: {
			backgroundColor: theme.palette.error.main,
			color: theme.palette.error.contrastText,
		},
	}))

	const EditInputCell = (props: GridRenderEditCellParams) => {
		const { error, errorMessage } = props

		const isError = error

		return (
			<StyledTooltip open={!!isError} title={errorMessage}>
				<GridEditInputCell
					{...props}
					css={isError ? { boxShadow: `inset 0 0 0 1px ${mono.error}` } : {}}
				/>
			</StyledTooltip>
		)
	}

	const renderEdit = (params: GridRenderEditCellParams) => {
		return <EditInputCell {...params} />
	}

	const rederCheckboxEdit = (params: GridRenderEditCellParams) => {
		return <GridEditBooleanCell {...params} />
	}

	const newColumns: GridColDef<GridValidRowModel>[] = React.useMemo(() => {
		const indexingCol: GridColDef = {
			field: 'idx',
			headerName: 'index',
			width: 80,
			headerAlign: 'center',
			align: 'center',
			filterable: false,
			renderCell: (res: GridRenderCellParams) =>
				res.api.getAllRowIds().indexOf(res.id) + 1,
		}

		// "자세히" — index 바로 오른쪽. 텍스트 버튼 한 단계(boBtnText 와 같은 무게)
		const detailCol: GridColDef = {
			field: 'detail',
			headerName: '자세히',
			width: 80,
			headerAlign: 'center',
			align: 'center',
			sortable: false,
			filterable: false,
			disableColumnMenu: true,
			renderCell: (res: GridRenderCellParams) => (
				<button
					type="button"
					className="gridDetailBtn"
					onClick={e => {
						e.stopPropagation()
						props.onDetailClick?.(res.row)
					}}
				>
					자세히
				</button>
			),
		}

		const actionCol: GridColDef = {
			field: 'actions',
			type: 'actions',
			headerName: '액션',
			width: 90,
			headerAlign: 'center',
			align: 'center',
			cellClassName: 'actions',
			getActions: (params: GridRowParams) => {
				return [
					<ActionButton
						style={{ margin: '0 0' }}
						key="rowActionDelete"
						iconName="trash-can-outline"
						onClick={onDeleteClick(params)}
						tooltip="삭제"
						title="삭제"
					/>,
				]
			},
		}

		const orderedColumns = (() => {
			const map = new Map(props.columns.columns.map(c => [c.field, c]))
			const ordered: GridColDef[] = []
			columnOrder.forEach(f => {
				const c = map.get(f)
				if (c) ordered.push(c)
			})
			// columnOrder에 없는 컬럼은 끝에 추가 (방어적)
			props.columns.columns.forEach(c => {
				if (!columnOrder.includes(c.field)) ordered.push(c)
			})
			return ordered
		})()

		const tempColumns = orderedColumns.map(aColumn => {
			const field = aColumn.field
			const newColumn =
				aColumn.preProcessEditCellProps !== undefined
					? { ...aColumn, renderEditCell: renderEdit }
					: aColumn.type === 'boolean'
					? { ...aColumn, renderEditCell: rederCheckboxEdit }
					: { ...aColumn }

			// 수정 모드 OFF일 땐 모든 셀 편집 비활성 (원래 editable=true 컬럼만 모드 ON 시 편집 가능)
			// alwaysEditable 플래그가 있으면 수정 모드와 무관하게 편집 허용
			if (!editModeOn && !(aColumn as any).alwaysEditable) {
				newColumn['editable'] = false
			}

			if (aColumn.type === 'date' || aColumn.type === 'dateTime') {
				newColumn['filterOperators'] = [
					...(aColumn.filterOperators ?? []),
					DATE_BETWEEN_OPERATOR,
				]
			}

			if (props.renderCell && props.renderCell[field] !== undefined) {
				newColumn['renderCell'] = props.renderCell[field]
			}

			// 수정 모드 전용 셀이 있으면 모드가 켜져 있는 동안 renderCell 을 그것으로 바꾼다.
			// (renderEditCell 로 두면 셀을 더블클릭해야 나온다 — 버튼에는 맞지 않는 동작)
			if (
				editModeOn &&
				props.renderEditModeCell &&
				props.renderEditModeCell[field] !== undefined
			) {
				newColumn['renderCell'] = props.renderEditModeCell[field]
			}

			if (props.renderEditCell && props.renderEditCell[field] !== undefined) {
				newColumn['renderEditCell'] = props.renderEditCell[field]
			}

			// 기본 sort 비활성 — sort는 자체 아이콘 버튼으로만 트리거
			newColumn['sortable'] = false

			// 헤더 = [컬럼명] [sort 아이콘 버튼] (메뉴 버튼은 default로 우측에 그려짐)
			if (newColumn.renderHeader === undefined) {
				newColumn['renderHeader'] = (params: any) => {
					const dir = sortModel.find(s => s.field === params.field)?.sort ?? null
					const iconName =
						dir === 'asc' ? 'sort-ascending' : dir === 'desc' ? 'sort-descending' : 'sort'
					return (
						<View
							css={{
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
								width: '100%',
								gap: '0.25rem',
							}}
						>
							<span
								css={{
									flex: 1,
									minWidth: 0,
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
									fontWeight: 500,
								}}
							>
								{/* 컬럼셋이 통째로 바뀌는 순간(정산 탭 전환) colDef 가 비어 올 수 있다 */}
								{params.colDef?.headerName ?? params.field}
							</span>
							<button
								type="button"
								className={`gridSortBtn${dir ? ' active' : ''}`}
								title={
									dir === 'asc'
										? '내림차순 정렬'
										: dir === 'desc'
										? '정렬 해제'
										: '오름차순 정렬'
								}
								onMouseDown={e => e.stopPropagation()}
								onClick={e => {
									e.stopPropagation()
									toggleSort(params.field)
								}}
								css={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									width: 22,
									height: 22,
									border: 'none',
									background: 'transparent',
									cursor: 'pointer',
									padding: 0,
									borderRadius: 2,
									color: dir ? mono.ink : mono.faint,
									'&:hover': { color: mono.ink, backgroundColor: mono.line },
								}}
							>
								<span
									className={`mdi mdi-${iconName}`}
									css={{ fontSize: 16, lineHeight: 1 }}
								/>
							</button>
						</View>
					)
				}
			}

			return newColumn
		})

		const fixedCols: GridColDef[] =
			props.onDetailClick !== undefined ? [indexingCol, detailCol] : [indexingCol]
		const res: GridColDef[] = isEditable
			? [actionCol, ...fixedCols, ...tempColumns]
			: [...fixedCols, ...tempColumns]

		return res
	}, [
		props.columns,
		props.onDetailClick,
		columnOrder,
		rows,
		props.rows,
		reorderColumn,
		startHeaderDrag,
		sortModel,
		toggleSort,
		editModeOn,
	])

	const onDeleteClick = (props: GridRowParams) => () => {
		const { row } = props

		setDeleteRow(row)
		setShowModal(true)
	}

	const onModalDelete = React.useCallback(() => {
		const newRows = rows.filter(aRow => {
			return aRow.id !== deleteRow.id
		})
		setRows(newRows)

		if (typeof props.onAction === 'function') {
			props.onAction('D', deleteRow)
		}
	}, [deleteRow])

	const processRowUpdate = (newRow: GridRowModel, oldRow: GridRowModel) => {
		const isNew = newRow.isNew !== undefined

		// cell 모드에서는 사용자가 별도로 isEdited 플래그를 세팅하지 않으므로 값 비교로 변경 감지
		let isChanged = false
		const allKeys = new Set<string>([
			...Object.keys(newRow),
			...Object.keys(oldRow ?? {}),
		])
		for (const key of allKeys) {
			if (key === 'isNew' || key === 'isEdited') continue
			if (newRow[key] !== (oldRow ?? {})[key]) {
				isChanged = true
				break
			}
		}

		if (isNew && typeof props.onAction === 'function') {
			props.onAction('I', newRow)
		} else if (isChanged && typeof props.onAction === 'function') {
			props.onAction('U', newRow)
		}

		const cleaned: GridRowModel = { ...newRow }
		delete cleaned.isNew
		delete cleaned.isEdited

		setRows(prev => prev.map(aRow => (aRow.id === newRow.id ? cleaned : aRow)))

		return cleaned
	}

	const onAddClick = () => {
		const id = randomId()
		setRows(oldRows => [...oldRows, { id: id, ...props.columns?.initRow, isNew: true }])
		const fieldToFocus = props.columns?.fieldToFocus
		if (fieldToFocus) {
			// 새 row가 그리드에 반영된 후 cell 편집 모드 진입
			setTimeout(() => {
				try {
					apiRef.current?.startCellEditMode({ id, field: fieldToFocus })
				} catch {
					/* noop */
				}
			}, 0)
		}
	}

	return (
		<View css={style}>
			{isLoading && (
				<View className="gridLoadingCircleView">
					<CircularProgress />
				</View>
			)}
			<View className={`gridContainer${editModeOn ? ' gridContainerEdit' : ''}`}>
				<View className="gridLeft">
					<DataGrid
						{...props}
						rowHeight={boSize.rowH}
						columnHeaderHeight={boSize.rowH}
						apiRef={apiRef}
						rows={rows}
						columns={newColumns}
						editMode="cell"
						sortingOrder={['asc', 'desc', null]}
						localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
						paginationModel={paginationModel}
						onPaginationModelChange={handlePaginationModelChange}
						pageSizeOptions={PAGE_SIZE_OPTIONS}
						slots={{
							toolbar: GridToolBar,
							pagination: CustomPagination,
						}}
						slotProps={{
							toolbar: {
								setRows,
								isLoading,
								apiRef,
								onAddClick,
								title: props.title,
								onExcelUpload: props.onExcelUpload,
								onExcelUploadClick: props.onExcelUploadClick,
								leftToolbarSlot: props.leftToolbarSlot,
								toolbarVariant: props.toolbarVariant ?? 'full',
								editModeOn,
								onEditModeToggle: setEditModeOn,
								hasActionColumn: isEditable,
							},
							loadingOverlay: {
								variant: 'skeleton',
								noRowsVariant: 'skeleton',
							},
						}}
						onRowSelectionModelChange={newRowSelectionModel => {
							if (typeof props.onRowSelectionModelChange === 'function') {
								props.onRowSelectionModelChange(newRowSelectionModel)
							}
						}}
						disableRowSelectionOnClick={checkboxSelection}
						checkboxSelection={checkboxSelection}
						rowSelectionModel={props.rowSelectionModel}
						indeterminateCheckboxAction="select"
						isRowSelectable={
							props.isRowSelectable ?? ((params: GridRowParams) => params.row.status)
						}
						onProcessRowUpdateError={() => {}}
						processRowUpdate={processRowUpdate}
						loading={isLoading}
						disableColumnSorting={isLoading}
						disableColumnMenu={isLoading}
						sx={{
							border: 'none',
							borderRadius: 0,
							fontFamily: boFont.ui,
							fontSize: 13,
							fontWeight: 300,
							color: mono.ink,
							backgroundColor: mono.surface,
							// 헤더 — surface 위 키커 라벨, 하단 헤어라인
							'& .MuiDataGrid-columnHeaders': {
								borderBottom: `1px solid ${mono.line}`,
							},
							'& .MuiDataGrid-columnHeader': {
								cursor: 'grab',
								backgroundColor: mono.surface,
								transition: 'background-color 200ms ease-out',
							},
							'& .MuiDataGrid-columnHeader:hover': {
								backgroundColor: mono.bg,
							},
							'& .MuiDataGrid-columnHeader:active': { cursor: 'grabbing' },
							'& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
								{
									outline: 'none',
								},
							'& .MuiDataGrid-columnHeaderTitle': {
								fontSize: 11,
								fontWeight: 500,
								letterSpacing: '.12em',
								textTransform: 'uppercase',
								color: mono.faint,
							},
							'& .MuiDataGrid-columnHeader .gridSortBtn, & .MuiDataGrid-columnHeader .MuiIconButton-root':
								{
									cursor: 'pointer',
								},
							'& .MuiDataGrid-menuIcon .MuiIconButton-root': { color: mono.faint },
							'& .MuiDataGrid-columnSeparator': {
								cursor: 'col-resize',
								color: mono.line,
							},
							'& .MuiDataGrid-columnHeaderTitleContainerContent': { flex: 1 },
							'& .MuiDataGrid-sortIcon': { display: 'none' },
							'& .MuiDataGrid-iconButtonContainer': {
								width: 0,
								visibility: 'hidden',
							},
							'& .gridSortBtn': { visibility: 'hidden' },
							'& .gridSortBtn.active': { visibility: 'visible' },
							'& .MuiDataGrid-columnHeader:hover .gridSortBtn': {
								visibility: 'visible',
							},
							'& .MuiDataGrid-columnHeader.colDropHover': {
								boxShadow: `inset 1px 0 0 ${mono.ink}`,
								backgroundColor: mono.bg,
							},
							// 행 — 헤어라인만. 얼룩말 줄무늬 없음, hover 는 그라운드색
							'& .MuiDataGrid-row': {
								transition: 'background-color 200ms ease-out',
							},
							'& .MuiDataGrid-row:hover': {
								backgroundColor: mono.bg,
							},
							'& .MuiDataGrid-row.Mui-selected': {
								backgroundColor: 'rgba(22, 24, 27, 0.05)',
							},
							'& .MuiDataGrid-row.Mui-selected:hover': {
								backgroundColor: 'rgba(22, 24, 27, 0.07)',
							},
							// 삭제·환불 등 비활성 행 — 색이 아니라 명도로 물러난다
							'& .MuiDataGrid-row.row-inactive': {
								color: mono.faint,
							},
							'& .MuiDataGrid-row.row-inactive:hover': {
								backgroundColor: mono.bg,
							},
							'& .MuiDataGrid-row.row-inactive.Mui-selected': {
								backgroundColor: 'rgba(22, 24, 27, 0.05)',
							},
							'& .MuiDataGrid-cell': {
								borderBottom: `1px solid ${mono.line}`,
								borderTop: 0,
								outline: 'none !important',
								fontVariantNumeric: 'tabular-nums',
							},
							'& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
								outline: 'none !important',
							},
							'& .MuiDataGrid-cell.actions': {
								padding: 0,
							},
							'& .MuiDataGrid-cell--editing': {
								backgroundColor: `${mono.surface} !important`,
								boxShadow: `inset 0 0 0 1px ${mono.ink}`,
							},
							'& .MuiDataGrid-editInputCell': { fontSize: 13, fontFamily: boFont.ui },
							'& .MuiDataGrid-overlay': { backgroundColor: mono.surface },
							'& .MuiDataGrid-virtualScroller': { backgroundColor: mono.surface },
							'& .MuiDataGrid-filler': { backgroundColor: mono.surface },
							// 푸터 — 상단 헤어라인, 페이지 그라운드
							'& .MuiDataGrid-footerContainer': {
								height: 44,
								minHeight: 44,
								borderTop: `1px solid ${mono.line}`,
								backgroundColor: mono.surface,

								'.MuiTablePagination-root': {
									height: '100%',
									minHeight: 44,
									color: mono.dim,
									fontSize: 12,

									'.MuiTablePagination-toolbar': {
										height: 44,
										minHeight: 44,
									},
								},
							},
							'& .MuiDataGrid-selectedRowCount': { fontSize: 12, color: mono.dim },
							'& .MuiPagination-root .MuiPaginationItem-root': {
								fontSize: 12,
								minWidth: 28,
								height: 28,
							},
							'& .MuiCheckbox-root': {
								color: mono.lineInput,
							},
							'& .MuiCheckbox-root.Mui-checked, & .MuiCheckbox-root.MuiCheckbox-indeterminate':
								{
									color: mono.ink,
								},
						}}
					/>
				</View>
				{sidePanelEnabled && (
					<GridSidePanel
						apiRef={apiRef}
						columns={newColumns}
						openTab={sidePanelTab}
						onChangeTab={setSidePanelTab}
						onReorder={reorderColumn}
					/>
				)}
			</View>

			<Modal
				target="newGroup"
				title="삭제"
				show={showModal}
				onSubmit={onModalDelete}
				onCloseModal={() => setShowModal(false)}
				closeStatus="inherit"
				closeTitle="닫기"
				submitTitle="삭제"
				submitStatus="error"
				submitIcon="trash-can-outline"
			>
				<View css={{ width: '30rem', maxWidth: '100%' }}>
					<Text category="p2">삭제한 데이터는 되돌릴 수 없다.</Text>
				</View>
			</Modal>
		</View>
	)
}

const style = css({
	width: '100%',
	height: '100%',
	position: 'relative',
	fontFamily: boFont.ui,

	'.gridContainer': {
		display: 'flex',
		flexDirection: 'row',
		width: '100%',
		height: '100%',
		minHeight: 0,
		overflow: 'hidden',
		boxSizing: 'border-box',
		backgroundColor: mono.surface,
		border: `1px solid ${mono.line}`,
		borderRadius: 0,
		boxShadow: 'none',
		transition: 'border-color 200ms ease-out',
	},

	// 수정 모드 — 외곽선이 ink 로 올라온다. 색이 아니라 선의 무게로 상태를 말한다
	'.gridContainerEdit': {
		borderColor: mono.ink,
	},

	'.gridContainerEdit .MuiDataGrid-cell--editable': {
		cursor: 'cell',
	},
	'.gridContainerEdit .MuiDataGrid-cell--editable:hover': {
		backgroundColor: `${mono.bg} !important`,
		boxShadow: `inset 0 0 0 1px ${mono.lineSoft}`,
	},
	'.gridContainerEdit .MuiDataGrid-cell--editing': {
		backgroundColor: `${mono.surface} !important`,
		boxShadow: `inset 0 0 0 1px ${mono.ink}`,
	},

	'.gridLeft': {
		flex: '1 1 0',
		minWidth: 0,
		width: 0,
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},

	// "자세히" 셀 — 밑줄 링크 무게. 색이 아니라 밑줄로 동작임을 말한다
	'.gridDetailBtn': {
		padding: 0,
		paddingBottom: 1,
		background: 'transparent',
		border: 0,
		borderBottom: `1px solid ${mono.lineSoft}`,
		fontFamily: boFont.ui,
		fontSize: 12,
		fontWeight: 500,
		lineHeight: 1.2,
		color: mono.dim,
		cursor: 'pointer',
		transition: 'color 200ms ease-out, border-color 200ms ease-out',
		'&:hover': { color: mono.ink, borderBottomColor: mono.ink },
		'&:focus-visible': { outline: `1px solid ${mono.ink}`, outlineOffset: 2 },
	},

	'.gridLoadingCircleView': {
		position: 'absolute',
		width: 40,
		height: 40,
		zIndex: 10,
		top: 0,
		bottom: 0,
		left: 0,
		right: 0,
		margin: 'auto',
	},
})
